import { AnimatePresence, motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  warningMessage?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'success' | 'info';
  icon?: LucideIcon;
  isLoading?: boolean;
}

const variantStyles = {
  danger: {
    iconBg: 'bg-rose-500/10',
    iconColor: 'text-rose-500',
    buttonBg: 'bg-rose-500',
    buttonShadow: 'shadow-rose-500/20',
    buttonHover: 'hover:bg-rose-600',
    warningBg: 'bg-rose-50 dark:bg-rose-900/20',
    warningBorder: 'border-rose-100 dark:border-rose-800',
    warningText: 'text-rose-700 dark:text-rose-300',
  },
  warning: {
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-500',
    buttonBg: 'bg-amber-500',
    buttonShadow: 'shadow-amber-500/20',
    buttonHover: 'hover:bg-amber-600',
    warningBg: 'bg-amber-50 dark:bg-amber-900/20',
    warningBorder: 'border-amber-100 dark:border-amber-800',
    warningText: 'text-amber-700 dark:text-amber-300',
  },
  success: {
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-500',
    buttonBg: 'bg-emerald-500',
    buttonShadow: 'shadow-emerald-500/20',
    buttonHover: 'hover:bg-emerald-600',
    warningBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    warningBorder: 'border-emerald-100 dark:border-emerald-800',
    warningText: 'text-emerald-700 dark:text-emerald-300',
  },
  info: {
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-500',
    buttonBg: 'bg-blue-500',
    buttonShadow: 'shadow-blue-500/20',
    buttonHover: 'hover:bg-blue-600',
    warningBg: 'bg-blue-50 dark:bg-blue-900/20',
    warningBorder: 'border-blue-100 dark:border-blue-800',
    warningText: 'text-blue-700 dark:text-blue-300',
  },
};

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  warningMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'warning',
  icon: Icon,
  isLoading = false,
}: ConfirmModalProps) {
  const styles = variantStyles[variant];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-8">
              {Icon && (
                <div
                  className={`w-16 h-16 mx-auto mb-6 rounded-full ${styles.iconBg} flex items-center justify-center`}
                >
                  <Icon className={`w-8 h-8 ${styles.iconColor}`} />
                </div>
              )}
              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                {title}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                {message}
              </p>
            </div>

            {warningMessage && (
              <div
                className={`p-4 rounded-2xl ${styles.warningBg} border ${styles.warningBorder} mb-8`}
              >
                <p className={`text-xs font-bold ${styles.warningText} leading-relaxed`}>
                  <strong className="font-black">Warning:</strong> {warningMessage}
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className={`flex-1 px-6 py-4 rounded-2xl ${styles.buttonBg} text-white font-black text-sm ${styles.buttonShadow} ${styles.buttonHover} transition-all disabled:opacity-50`}
              >
                {isLoading ? 'Processing...' : confirmLabel}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
